import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { Reveal } from '@/components/generals/reveal'
import { Eyebrow } from '@/components/generals/eyebrow'

/*
   The estate
   Editorial two-column introduction: a large serif statement on the left,
   the story and two key figures on the right, then a wide photograph of the
   private estate road.
*/
export const EstateSection: FC = (): ReactNode => {
    return (
        <section id='estate' aria-labelledby='estate-heading' className='bg-cream'>
            <div className='mx-auto max-w-7xl px-6 pb-24 lg:px-10 lg:pb-32'>
                <div className='grid gap-12 border-t border-stone pt-20 lg:grid-cols-12 lg:gap-10 lg:pt-28'>
                    <Reveal className='lg:col-span-6'>
                        <Eyebrow>
                            The estate
                        </Eyebrow>

                        <h2 id='estate-heading' className='mt-6 font-heading text-4xl leading-[1.08] sm:text-5xl lg:text-[3.5rem]'>
                            Quiet luxury,
                            <br />
                            <em className='italic text-forest'>modern heritage.</em>
                        </h2>
                    </Reveal>

                    <Reveal delay={120} className='lg:col-span-5 lg:col-start-8'>
                        <div className='space-y-5 text-[1.0625rem] leading-relaxed text-charcoal/80'>
                            <p>
                                Some addresses announce themselves. Rockwood prefers to be discovered. Set behind a secure gate in central Hillcrest, the estate is a private enclave of just fourteen freestanding homes, each one single-storey and finished with the kind of restraint that only grows better with time.
                            </p>

                            <p>
                                Natural textures, generous light and a calm, considered palette nod to Hillcrest's village heritage, while the plans themselves are thoroughly modern: open, easy and entirely on one level.
                            </p>
                        </div>

                        <dl className='mt-10 grid grid-cols-2 gap-6 border-t border-stone pt-8'>
                            <div>
                                <dt className='text-[0.6875rem] uppercase tracking-[0.2em] text-muted-foreground'>
                                    Homes, and no more
                                </dt>

                                <dd className='mt-2 font-heading text-5xl text-forest'>
                                    14
                                </dd>
                            </div>

                            <div>
                                <dt className='text-[0.6875rem] uppercase tracking-[0.2em] text-muted-foreground'>
                                    Storeys, by design
                                </dt>

                                <dd className='mt-2 font-heading text-5xl text-forest'>
                                    1
                                </dd>
                            </div>
                        </dl>
                    </Reveal>
                </div>

                <Reveal className='mt-16 lg:mt-24'>
                    <figure className='group relative overflow-hidden rounded-3xl bg-sand'>
                        <div className='relative aspect-[4/3] sm:aspect-[16/9] lg:aspect-[21/9]'>
                            <Image
                                src='/images/estate-street.jpg'
                                alt='A quiet private road lined with freestanding single-storey homes, each with a double garage'
                                fill
                                sizes='(min-width: 1280px) 1216px, 100vw'
                                className='object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.03]'
                            />
                        </div>

                        <figcaption className='absolute bottom-0 left-0 m-4 rounded-full bg-cream/90 px-4 py-2 text-xs font-medium text-charcoal backdrop-blur sm:m-6'>
                            One private road. Fourteen homes. One secure gate.
                        </figcaption>
                    </figure>
                </Reveal>
            </div>
        </section>
    )
}
